import { Injectable, inject } from "@angular/core";
import type { CourseDetail } from "@core/models/course-detail.model";
import { CoursesService } from "../api-service/courses.service";
import { Observable, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { TermsConditions } from "@core/models/terms-conditions.model";
import { TermsConditionsPositionEnum } from "@core/enums/terms-conditions-position.enum";
import { ResponseDataModel } from "@core/models/response-data.model";

export interface LoadDetailedCourseStateOptions {
  force?: boolean;
  courseId: number;
}

@Injectable({
  providedIn: "root",
})
export class DetailedCourseStateService {
  private readonly coursesService: CoursesService = inject(CoursesService);

  public courseId!: number;
  public courseData!: CourseDetail;

  public markCourseTermsConfirmedLocally(courseId: number, positions: readonly TermsConditionsPositionEnum[]): void {
    if (this.courseId !== courseId || !this.courseData?.termsConditions?.length || positions.length === 0) return;

    const updatedTermsConditions: TermsConditions[] = this.courseData.termsConditions.map((term: TermsConditions) =>
      positions.includes(term.position) ? { ...term, hasUserConfirm: true } : term,
    );

    this.courseData = { ...this.courseData, termsConditions: updatedTermsConditions };
  }

  public loadCourseData$(options: LoadDetailedCourseStateOptions): Observable<CourseDetail> {
    const force: boolean = options?.force ?? false;

    this.courseId = options.courseId;

    if (!force && this.hasCachedCourseForCurrentId()) return of(this.courseData);

    return this.coursesService.getCourseById(this.courseId).pipe(
      map((response: ResponseDataModel<CourseDetail>) => response.data),
      tap((data: CourseDetail) => (this.courseData = data)),
    );
  }

  public isCourseCached(courseId: number): boolean {
    return this.courseData?.id === courseId;
  }

  private hasCachedCourseForCurrentId(): boolean {
    return !!(this.courseData?.id === this.courseId);
  }
}
